import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Building2 } from "lucide-react";
import { API } from "../../lib/api";
import { money } from "../../lib/format";
import { Loading } from "../../components/common";

export default function Pricing() {
  const [plans, setPlans] = useState(null);
  const [interval, setInterval_] = useState("monthly");
  const [, setParams] = useSearchParams();

  useEffect(() => {
    API.get("/public/plans").then((r) => setPlans(r.data.plans)).catch(() => setPlans([]));
  }, []);

  return (
    <div>
      <section className="border-b border-slate-200 bg-white py-14">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-copper">Pricing</div>
          <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">Simple Plans, Serious Compliance</h1>
          <p className="mx-auto mt-4 max-w-xl text-slate-600">Every plan includes the Tax Vault, audit logging, and secure document storage. Cancel anytime.</p>
          <div className="mt-6 inline-flex rounded-md border border-slate-300 bg-white p-1" data-testid="billing-interval-toggle">
            {[["monthly", "Monthly"], ["yearly", "Annual — save ~20%"]].map(([v, l]) => (
              <button key={v} onClick={() => setInterval_(v)} data-testid={`interval-${v}`}
                className={`rounded px-4 py-1.5 text-sm font-semibold ${interval === v ? "bg-navy text-white" : "text-slate-600"}`}>{l}</button>
            ))}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        {plans === null ? <Loading label="Loading plans" /> : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {plans.map((p) => {
              const price = interval === "monthly" ? p.price_monthly_cents : p.price_yearly_cents;
              const featured = p.slug === "business";
              return (
                <div key={p.id} className={`card-gov relative flex flex-col p-6 ${featured ? "border-2 border-gold shadow-md" : ""}`} data-testid={`plan-${p.slug}`}>
                  {featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold px-3 py-0.5 text-xs font-bold text-navy-dark">MOST POPULAR</span>}
                  <h3 className="text-lg font-bold text-navy">{p.name}</h3>
                  <p className="mt-1 text-xs text-slate-500">{p.tagline}</p>
                  <div className="mt-4">
                    {p.custom || price == null ? (
                      <div className="flex items-center gap-2 text-2xl font-bold text-navy"><Building2 className="h-5 w-5 text-gold" /> Custom Pricing</div>
                    ) : (
                      <>
                        <span className="money text-3xl font-bold text-navy">{money(price)}</span>
                        <span className="text-sm text-slate-500">/{interval === "monthly" ? "mo" : "yr"}</span>
                        {p.trial_days > 0 && <div className="mt-1 text-xs font-semibold text-emerald-700">{p.trial_days}-day trial included</div>}
                      </>
                    )}
                  </div>
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex gap-2 text-sm text-slate-600">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{f}
                      </li>
                    ))}
                  </ul>
                  {p.custom ? (
                    <Link to="/consultation" className="btn-outline-gov mt-6 w-full" data-testid={`plan-cta-${p.slug}`}>Contact Sales</Link>
                  ) : (
                    <Link to={`/register?plan=${p.slug}&interval=${interval}`}
                      className={`mt-6 w-full ${featured ? "btn-copper" : "btn-navy"}`} data-testid={`plan-cta-${p.slug}`}>
                      Get Started
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-8 text-center text-xs text-slate-400">
          Subscription billing is processed securely by Stripe. AZ Tax Accounts subscription charges are completely separate from your government tax payments.
        </p>
      </section>
    </div>
  );
}
